import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Stack, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Chip, IconButton, Switch, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, MenuItem, CircularProgress, Alert, Avatar, Tabs, Tab, Divider,
} from '@mui/material';
import {
  AdminPanelSettings, People, CheckCircle, Paid, Search, Edit, Whatshot, TrendingUp,
  Forum, ArrowBack, SmartToy, Person,
} from '@mui/icons-material';
import { api } from '../config/api';
import { useAuth } from '../context/AuthContext';
import AdminPagos from './admin/AdminPagos';
import AdminPlanes from './admin/AdminPlanes';
import AdminCupones from './admin/AdminCupones';

const clp = (n: any) => `$${Math.round(Number(n || 0)).toLocaleString('es-CL')}`;
const fecha = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-CL', { dateStyle: 'medium' }) : '—');
const ESTADO_COLOR: Record<string, any> = { activa: 'success', trial: 'info', gracia: 'warning', vencida: 'error', cancelada: 'default' };

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [resumen, setResumen] = useState<any>(null);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [buscar, setBuscar] = useState('');
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<any>(null);
  const [detalle, setDetalle] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [tab, setTab] = useState(0);
  const [dias, setDias] = useState('30');
  const [planAsignar, setPlanAsignar] = useState('');
  const [planes, setPlanes] = useState<any[]>([]);
  const [saldoDelta, setSaldoDelta] = useState('');
  const [saldoMotivo, setSaldoMotivo] = useState('');

  // Soporte: ver conversaciones de un tenant (solo lectura)
  const [convTenant, setConvTenant] = useState<any>(null);      // tenant cuyo chat estamos viendo
  const [conversaciones, setConversaciones] = useState<any[]>([]);
  const [convSel, setConvSel] = useState<any>(null);            // conversación abierta
  const [mensajes, setMensajes] = useState<any[]>([]);
  const [cargandoConv, setCargandoConv] = useState(false);

  const cargar = (q = '') => {
    setLoading(true);
    Promise.all([
      api.get('/admin/resumen'),
      api.get('/admin/usuarios', { params: { buscar: q } }),
    ]).then(([r, u]) => { setResumen(r.data); setUsuarios(u.data.usuarios || []); }).finally(() => setLoading(false));
  };
  useEffect(() => { cargar(); api.get('/admin/planes').then((r) => setPlanes(r.data.planes || [])).catch(() => {}); }, []);

  const abrir = async (u: any) => {
    setSel(u);
    if (sel?.id !== u.id) { setPlanAsignar(''); setSaldoDelta(''); setSaldoMotivo(''); }
    const { data } = await api.get(`/admin/usuarios/${u.id}`);
    setDetalle(data);
  };

  const toggleActivo = async (u: any, activo: boolean) => {
    await api.post(`/admin/usuarios/${u.id}/activo`, { activo });
    setMsg(`Usuario ${activo ? 'habilitado' : 'deshabilitado'}`); cargar(buscar);
  };
  const cambiarSub = async (estado: string, d?: number) => {
    setErrorMsg('');
    try {
      await api.post(`/admin/usuarios/${sel.id}/suscripcion`, { estado, dias: d, plan: planAsignar || undefined });
      setMsg('Suscripción actualizada'); abrir(sel); cargar(buscar);
    } catch (e: any) { setErrorMsg(e.response?.data?.error || 'No se pudo actualizar'); }
  };
  const cambiarRol = async (rol: string) => {
    await api.post(`/admin/usuarios/${sel.id}/rol`, { rol });
    setMsg('Rol actualizado'); abrir(sel); cargar(buscar);
  };
  const ajustarSaldo = async (signo: 1 | -1) => {
    setErrorMsg('');
    const monto = parseInt(saldoDelta, 10);
    if (!monto || monto <= 0) { setErrorMsg('Ingresa un monto mayor a 0'); return; }
    try {
      await api.post(`/admin/usuarios/${sel.id}/saldo`, { monto: signo * monto, motivo: saldoMotivo || undefined });
      setSaldoDelta(''); setSaldoMotivo('');
      setMsg('Saldo actualizado'); abrir(sel); cargar(buscar);
    } catch (e: any) { setErrorMsg(e.response?.data?.error || 'No se pudo ajustar el saldo'); }
  };

  // ─── Soporte: conversaciones del tenant (solo lectura) ───
  const verConversaciones = async (u: any) => {
    setConvTenant(u); setConvSel(null); setMensajes([]); setCargandoConv(true);
    try {
      const { data } = await api.get(`/admin/usuarios/${u.id}/conversaciones`);
      setConversaciones(data.conversaciones || []);
    } finally { setCargandoConv(false); }
  };
  const verMensajes = async (c: any) => {
    setConvSel(c); setCargandoConv(true);
    try {
      const { data } = await api.get(`/admin/usuarios/${convTenant.id}/conversaciones/${c.id}/mensajes`);
      setMensajes(data.mensajes || []);
    } finally { setCargandoConv(false); }
  };
  const cerrarConversaciones = () => {
    setConvTenant(null); setConversaciones([]); setConvSel(null); setMensajes([]);
  };

  if (user?.rol !== 'admin') return <Alert severity="error">Acceso restringido. Solo administradores.</Alert>;
  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  const kpis = [
    { label: 'Usuarios', value: resumen?.usuarios ?? 0, icon: <People />, color: '#3B82F6' },
    { label: 'Activos', value: resumen?.activos ?? 0, icon: <CheckCircle />, color: '#10B981' },
    { label: 'En prueba', value: resumen?.trials ?? 0, icon: <Whatshot />, color: '#F59E0B' },
    { label: 'Pagando', value: resumen?.pagadas ?? 0, icon: <Paid />, color: '#8B5CF6' },
    { label: 'Ingresos totales', value: clp(resumen?.ingresos), icon: <TrendingUp />, color: '#25D366' },
  ];
  const m = resumen?.metricas || {};
  const metricas = [
    { label: 'MRR', value: clp(m.mrr), sub: `ARPU ${clp(m.arpu)}` },
    { label: 'Ingresos del mes', value: clp(m.ingresosMes), sub: `Mes anterior ${clp(m.ingresosMesAnterior)}` },
    { label: 'Churn 30 días', value: `${m.churnTasa30 ?? 0}%`, sub: `${m.churn30 ?? 0} bajas · ${m.cancelando ?? 0} cancelando` },
    { label: 'Conversión a pago', value: `${m.conversion ?? 0}%`, sub: `${m.trials ?? 0} en prueba` },
    { label: 'En gracia / vencidos', value: `${m.enGracia ?? 0} / ${m.vencidas ?? 0}`, sub: `${m.cobroAutomatico ?? 0} con cobro automático` },
    { label: 'Pagos por revisar', value: `${m.pagosEnRevision ?? 0}`, sub: `${m.pagosPendientes ?? 0} pendientes · reemb. ${clp(m.reembolsos)}` },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AdminPanelSettings sx={{ color: '#D4AF37' }} />
        <Typography variant="h4">Panel de Administración</Typography>
        <Chip label="Admin" size="small" sx={{ bgcolor: '#D4AF37', color: '#fff' }} />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Gestión global de la plataforma: usuarios, suscripciones, saldos y métricas.
      </Typography>

      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((k) => (
          <Grid item xs={6} md={2.4} key={k.label}>
            <Card><CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                  <Typography variant="h5">{k.value}</Typography>
                </Box>
                <Box sx={{ color: k.color, bgcolor: `${k.color}18`, p: 1, borderRadius: 2, display: 'flex' }}>{k.icon}</Box>
              </Stack>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metricas.map((k) => (
          <Grid item xs={6} md={2} key={k.label}>
            <Card sx={{ height: '100%' }}><CardContent>
              <Typography variant="body2" color="text.secondary">{k.label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{k.value}</Typography>
              <Typography variant="caption" color="text.secondary">{k.sub}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="Usuarios" /><Tab label="Pagos" /><Tab label="Planes" /><Tab label="Cupones" />
      </Tabs>
      {tab === 1 && <AdminPagos onCambio={() => cargar(buscar)} />}
      {tab === 2 && <AdminPlanes />}
      {tab === 3 && <AdminCupones />}

      {tab === 0 && <Card>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField size="small" fullWidth placeholder="Buscar por nombre o email..." value={buscar}
              onChange={(e) => setBuscar(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') cargar(buscar); }}
              InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }} />
            <Button variant="outlined" onClick={() => cargar(buscar)}>Buscar</Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell><TableCell>Rol</TableCell><TableCell>Suscripción</TableCell>
                  <TableCell>Conv.</TableCell><TableCell>Leads</TableCell><TableCell>Activo</TableCell><TableCell>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 13 }}>{(u.nombre || '?')[0]}</Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.nombre}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Chip size="small" label={u.rol} color={u.rol === 'admin' ? 'warning' : 'default'} /></TableCell>
                    <TableCell><Chip size="small" label={u.sub_estado || 'sin'} color={ESTADO_COLOR[u.sub_estado] || 'default'} />{u.saldo > 0 && <Typography variant="caption" display="block" color="text.secondary">Saldo {clp(u.saldo)}</Typography>}</TableCell>
                    <TableCell>{u.conversaciones}</TableCell>
                    <TableCell>{u.leads}</TableCell>
                    <TableCell><Switch size="small" checked={u.activo} onChange={(e) => toggleActivo(u, e.target.checked)} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => abrir(u)} title="Editar"><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => verConversaciones(u)} title="Ver conversaciones (soporte)"><Forum fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {usuarios.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin usuarios</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>}

      {/* Detalle del usuario */}
      <Dialog open={!!sel} onClose={() => { setSel(null); setDetalle(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{sel?.nombre} — {sel?.email}</DialogTitle>
        <DialogContent>
          {!detalle ? <CircularProgress /> : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Licencia</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip color={ESTADO_COLOR[detalle.licencia?.estado] || 'default'} label={detalle.licencia?.estado || 'sin suscripción'} />
                  <Chip variant="outlined" label={detalle.licencia?.vigente ? 'El bot responde' : 'El bot NO responde'} color={detalle.licencia?.vigente ? 'success' : 'error'} />
                  <Typography variant="body2">{detalle.licencia?.plan?.nombre} · hasta {fecha(detalle.licencia?.graciaHasta || detalle.licencia?.fin)}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {detalle.licencia?.cancelarAlFin ? 'Cancelación programada al fin del periodo. ' : ''}
                  {detalle.licencia?.cobroAutomatico ? `Cobro automático con tarjeta ${detalle.licencia?.tarjeta?.ultimos4 || ''}. ` : ''}
                  {detalle.suscripcion?.intentos_cobro > 0 ? `Cobros fallidos: ${detalle.suscripcion.intentos_cobro}. ` : ''}
                  Uso del mes: {detalle.uso?.mensajes_bot || 0} respuestas, {((detalle.uso?.tokens_entrada || 0) + (detalle.uso?.tokens_salida || 0)).toLocaleString('es-CL')} tokens.
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Gestionar suscripción</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField size="small" type="number" label="Días" value={dias} onChange={(e) => setDias(e.target.value)} sx={{ width: 90 }} />
                  <TextField size="small" select label="Plan" value={planAsignar || detalle.licencia?.plan?.codigo || ''} onChange={(e) => setPlanAsignar(e.target.value)} sx={{ minWidth: 150 }}>
                    {planes.map((p) => <MenuItem key={p.codigo} value={p.codigo}>{p.nombre}</MenuItem>)}
                  </TextField>
                  <Button size="small" variant="contained" onClick={() => cambiarSub('activa', parseInt(dias, 10) || undefined)}>Activar / extender</Button>
                  <Button size="small" variant="outlined" onClick={() => cambiarSub('trial', parseInt(dias, 10) || undefined)}>Dar prueba</Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" color="warning" onClick={() => { if (window.confirm('¿Dejar la suscripción vencida? El bot deja de responder.')) cambiarSub('vencida'); }}>Vencer ahora</Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => { if (window.confirm('¿Cancelar la suscripción? Se apaga el agente y el cobro automático.')) cambiarSub('cancelada'); }}>Cancelar</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">"Activar" suma los días al periodo vigente. "Dar prueba" fija el fin de la prueba en hoy + días.</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Rol</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Button size="small" variant={detalle.usuario.rol !== 'admin' ? 'contained' : 'outlined'} onClick={() => cambiarRol('usuario')}>Usuario</Button>
                  <Button size="small" variant={detalle.usuario.rol === 'admin' ? 'contained' : 'outlined'} onClick={() => cambiarRol('admin')}>Admin</Button>
                </Stack>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Saldo: {clp(detalle.meta?.saldo_clp)}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField size="small" type="number" label="Monto" value={saldoDelta} onChange={(e) => setSaldoDelta(e.target.value)} sx={{ width: 120 }} />
                  <TextField size="small" label="Motivo" value={saldoMotivo} onChange={(e) => setSaldoMotivo(e.target.value)} sx={{ flex: 1, minWidth: 140 }} />
                  <Button size="small" variant="outlined" onClick={() => ajustarSaldo(1)}>Sumar</Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => ajustarSaldo(-1)}>Restar</Button>
                </Stack>
                {detalle.movimientos?.slice(0, 5).map((mv: any) => (
                  <Typography key={mv.id} variant="caption" display="block" color="text.secondary">
                    {fecha(mv.created_at)} · {mv.tipo} {mv.monto > 0 ? '+' : ''}{clp(mv.monto)} → {clp(mv.saldo_resultante)} · {mv.descripcion}
                  </Typography>
                ))}
              </Box>
              {detalle.pagos?.length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary">Últimos pagos</Typography>
                  {detalle.pagos.slice(0, 6).map((p: any) => (
                    <Typography key={p.id} variant="body2">
                      {fecha(p.pagado_at || p.created_at)} · {clp(p.monto)} · {p.tipo} · <b>{p.estado}</b>{p.reembolso_estado ? ` · reembolso ${p.reembolso_estado}` : ''} <Typography component="span" variant="caption" color="text.secondary">{p.commerce_order}</Typography>
                    </Typography>
                  ))}
                </Box>
              )}
              {detalle.eventos?.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="overline" color="text.secondary">Historial de la suscripción</Typography>
                  {detalle.eventos.slice(0, 10).map((ev: any, i: number) => (
                    <Typography key={i} variant="caption" display="block">
                      {new Date(ev.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })} · {ev.evento}
                      {ev.estado_nuevo ? ` → ${ev.estado_nuevo}` : ''} · {ev.actor}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => { setSel(null); setDetalle(null); }}>Cerrar</Button></DialogActions>
      </Dialog>

      {/* Conversaciones del tenant — SOLO LECTURA (soporte) */}
      <Dialog open={!!convTenant} onClose={cerrarConversaciones} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            {convSel && (
              <IconButton size="small" onClick={() => { setConvSel(null); setMensajes([]); }}><ArrowBack fontSize="small" /></IconButton>
            )}
            <Forum sx={{ color: '#25D366' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {convSel ? (convSel.nombre_contacto || convSel.telefono) : `Conversaciones de ${convTenant?.nombre}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {convSel ? convSel.telefono : convTenant?.email}
              </Typography>
            </Box>
            <Chip label="Solo lectura" size="small" sx={{ ml: 'auto', bgcolor: '#E2E8F0' }} />
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 360, bgcolor: convSel ? '#ECE5DD' : 'inherit' }}>
          {cargandoConv ? (
            <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>
          ) : convSel ? (
            // Vista de mensajes estilo chat
            <Stack spacing={1} sx={{ py: 1 }}>
              {mensajes.length === 0 && <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>Sin mensajes.</Typography>}
              {mensajes.map((m) => {
                const entrante = m.direccion === 'entrante';
                return (
                  <Box key={m.id} sx={{ display: 'flex', justifyContent: entrante ? 'flex-start' : 'flex-end' }}>
                    <Box sx={{
                      maxWidth: '75%', p: 1.2, borderRadius: 2,
                      bgcolor: entrante ? '#fff' : '#D9FDD3',
                      boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                    }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.3 }}>
                        {entrante ? <Person sx={{ fontSize: 14, color: '#64748B' }} /> : <SmartToy sx={{ fontSize: 14, color: '#128C7E' }} />}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: entrante ? '#64748B' : '#128C7E' }}>
                          {entrante ? 'Cliente' : (m.origen === 'agente' ? 'Agente' : 'Bot')}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.mensaje}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.3 }}>
                        {(m.created_at || '').replace('T', ' ').slice(0, 16)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            // Lista de conversaciones
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contacto</TableCell><TableCell>Último mensaje</TableCell>
                    <TableCell align="center">Lead</TableCell><TableCell align="center">Msgs</TableCell><TableCell>Actualizado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversaciones.map((c) => (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => verMensajes(c)}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.nombre_contacto || 'Sin nombre'}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.telefono}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{c.ultimo_mensaje || '—'}</Typography></TableCell>
                      <TableCell align="center">{c.es_lead ? <Chip size="small" label={`${c.score_interes}`} color="success" /> : '—'}</TableCell>
                      <TableCell align="center">{c.total_mensajes}</TableCell>
                      <TableCell><Typography variant="caption">{(c.updated_at || '').replace('T', ' ').slice(0, 16)}</Typography></TableCell>
                    </TableRow>
                  ))}
                  {conversaciones.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>Este usuario no tiene conversaciones.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions><Button onClick={cerrarConversaciones}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;
